import { createFirebaseAuthClient, type AuthClient } from '@pa/staff'
import { SiteNotConfigured } from '@pa/ui'
import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { createClinicApp } from './create'
import { parseEnv, type StaffEnv } from './env'

const firebaseFor = (env: StaffEnv): AuthClient =>
  createFirebaseAuthClient({
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    appId: env.VITE_FIREBASE_APP_ID,
    emulatorHost: env.VITE_FIREBASE_AUTH_EMULATOR_HOST,
  })

/**
 * Validates the environment, configures the API once and mounts the app (Frontend §1.6). An invalid config
 * renders "This site isn't configured" and logs the zod issues. Tests pass their own `authClient`.
 */
export async function boot(
  rawEnv: Record<string, unknown>,
  container: HTMLElement,
  options: { authClient?: AuthClient } = {},
): Promise<Root> {
  const root = createRoot(container)
  const result = parseEnv(rawEnv)
  if (!result.ok) {
    const summary = result.issues.map((i) => `${i.path.join('.') || '(env)'}: ${i.message}`).join('\n')
    console.error(`This site isn't configured. Invalid environment:\n${summary}`, result.issues)
    root.render(<SiteNotConfigured />)
    return root
  }
  const { env } = result
  // `import.meta.env.VITE_ENV` is replaced at build time, so a production build drops the mock chunk (task 19).
  if (import.meta.env.VITE_ENV !== 'prod' && env.VITE_API_MOCK) {
    const { startMockWorker } = await import('./mock')
    await startMockWorker(env.VITE_API_ORIGIN)
  }
  const app = createClinicApp({ env, authClient: options.authClient ?? firebaseFor(env) })
  root.render(<StrictMode>{app.element}</StrictMode>)
  return root
}
